import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faChevronRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import NavigationBar from "components/Navigation";
import { facilityService, FacilityDetail } from "services/facilityService";
import {
  SearchContainer,
  SearchSection,
  SearchInputBox,
  SearchInput,
  SearchIcon,
  ClearButton,
  CancelButton,
  SearchContent,
  SearchResults,
  ResultCard,
  ResultImage,
  ResultInfo,
  ResultHeader,
  ResultTitle,
  ResultArrow,
  ResultLocation,
  ResultTime,
  ReserveButton,
  EmptyMessage
} from "./SearchResult.styles";

const GUEST_SEARCH_KEY = "guestRecentSearches";
const MAX_GUEST_SEARCHES = 10;

const saveGuestSearch = (keyword: string) => {
  const saved = localStorage.getItem(GUEST_SEARCH_KEY);
  const history: string[] = saved ? JSON.parse(saved) : [];
  const updated = [keyword, ...history.filter(s => s !== keyword)].slice(0, MAX_GUEST_SEARCHES);
  localStorage.setItem(GUEST_SEARCH_KEY, JSON.stringify(updated));
};

const SearchResult: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState<string>(initialQuery);
  const [searchResults, setSearchResults] = useState<FacilityDetail[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const isGuest = localStorage.getItem("guestMode") === "true";

  const formatOperationTime = (operationTime: any[]): string => {
    if (!operationTime || operationTime.length === 0) {
      return "운영시간 정보 없음";
    }
    const firstTime = operationTime[0];
    return `${firstTime.startTime?.substring(0, 5)} ~ ${firstTime.endTime?.substring(0, 5)}`;
  };

  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.trim()) {
        setIsLoading(true);
        try {
          const results = await facilityService.searchFacilities(searchQuery);
          setSearchResults(results);
        } catch (error) {
          console.error("Search failed:", error);
          setSearchResults([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSearchResults([]);
      }
    };

    performSearch();
  }, [searchQuery]);

  useEffect(() => {
    if (initialQuery.trim()) {
      if (isGuest) {
        saveGuestSearch(initialQuery);
      } else {
        facilityService.saveSearchLog(initialQuery).catch(console.error);
      }
    }
  }, [initialQuery, isGuest]);

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      if (isGuest) {
        saveGuestSearch(searchQuery);
      } else {
        try {
          await facilityService.saveSearchLog(searchQuery);
        } catch (error) {
          console.error("Failed to save search log:", error);
        }
      }
      navigate(`/search-results?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleCancel = () => {
    navigate('/search');
  };

  const handleFacilityClick = async (facilityId: number) => {
    if (searchQuery.trim()) {
      if (isGuest) {
        saveGuestSearch(searchQuery);
      } else {
        try {
          await facilityService.saveSearchLog(searchQuery);
        } catch (error) {
          console.error("Failed to save search log:", error);
        }
      }
    }
    navigate(`/facilities/${facilityId}`);
  };

  const handleReserveClick = async (facilityId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (searchQuery.trim()) {
      if (isGuest) {
        saveGuestSearch(searchQuery);
      } else {
        try {
          await facilityService.saveSearchLog(searchQuery);
        } catch (error) {
          console.error("Failed to save search log:", error);
        }
      }
    }
    navigate(`/facilities/${facilityId}/reserve`);
  };

  return (
    <>
      <SearchContainer>
        <SearchSection>
          <SearchInputBox>
            <SearchInput
              type="text"
              placeholder="검색어를 입력하세요"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              autoFocus
            />
            {searchQuery && (
              <ClearButton onClick={handleClearSearch}>
                <FontAwesomeIcon icon={faXmark} />
              </ClearButton>
            )}
            <SearchIcon>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </SearchIcon>
          </SearchInputBox>
          <CancelButton onClick={handleCancel}>취소</CancelButton>
        </SearchSection>

        <SearchContent>
          {isLoading ? (
            <EmptyMessage>검색 중...</EmptyMessage>
          ) : searchResults.length > 0 ? (
            <SearchResults>
              {searchResults.map((result) => (
                <ResultCard key={result.id}>
                  <ResultImage>
                    <img src={result.imageUrl} alt={result.name} />
                  </ResultImage>
                  <ResultInfo>
                    <ResultHeader>
                      <ResultTitle>{result.name}</ResultTitle>
                      <ResultArrow onClick={() => handleFacilityClick(result.id)}>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </ResultArrow>
                    </ResultHeader>
                    <ResultLocation>{result.building.name}</ResultLocation>
                    <ResultTime>{formatOperationTime(result.operationTime)}</ResultTime>
                    <ReserveButton onClick={(e) => handleReserveClick(result.id, e)}>
                      예약하기
                    </ReserveButton>
                  </ResultInfo>
                </ResultCard>
              ))}
            </SearchResults>
          ) : searchQuery ? (
            <EmptyMessage>검색 결과가 없습니다.</EmptyMessage>
          ) : (
            <EmptyMessage>검색어를 입력해주세요.</EmptyMessage>
          )}
        </SearchContent>
      </SearchContainer>
      <NavigationBar />
    </>
  );
};

export default SearchResult;
